<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { SupportTicketCategory } from '@/types/support'
import type { SupportTicket } from '@/types/support'
import { useSupportTicket } from '@/composables/useSupportTicket'
import { useSupportAttachments } from '@/composables/useSupportAttachments'
import MarkdownEditor from '@/components/common/MarkdownEditor.vue'
import MultiFileDropZone from '@/components/common/MultiFileDropZone.vue'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import SlideOver from '@/components/shared/SlideOver.vue'

const props = defineProps<{
  organisationId: number
  open: boolean
}>()

const emit = defineEmits<{
  close: []
  created: [ticket: SupportTicket]
}>()

const { t } = useI18n()

const { create, submitting, formErrors } = useSupportTicket()
const { uploads, attachmentIds, upload, remove, reset: resetAttachments } = useSupportAttachments(props.organisationId)

// Form state
const category = ref<string>('')
const subject = ref('')
const body = ref('')
const files = ref<File[]>([])

// Local validation errors
const localErrors = ref<Record<string, string>>({})

const categories = computed(() => [
  { value: SupportTicketCategory.TechnicalIssue, label: t('support.category.technical_issue') },
  { value: SupportTicketCategory.RegistrationProblem, label: t('support.category.registration_problem') },
  { value: SupportTicketCategory.BillingAndPayments, label: t('support.category.billing_and_payments') },
  { value: SupportTicketCategory.AccountAndProfile, label: t('support.category.account_and_profile') },
  { value: SupportTicketCategory.GeneralQuestion, label: t('support.category.general_question') },
  { value: SupportTicketCategory.FeatureRequest, label: t('support.category.feature_request') },
])


function handleClose(): void {
  if (submitting.value) return
  emit('close')
}

function resetForm(): void {
  category.value = ''
  subject.value = ''
  body.value = ''
  files.value = []
  localErrors.value = {}
  resetAttachments()
}

async function handleFileAdd(file: File): Promise<void> {
  await upload(file)
}

function handleFileRemove(tempId: string): void {
  remove(tempId)
}

function validate(): boolean {
  const errs: Record<string, string> = {}
  if (!category.value) errs.category = t('support.form.error_category_required')
  if (!subject.value.trim()) errs.subject = t('support.form.error_subject_required')
  if (!body.value.trim()) errs.description = t('support.form.error_description_required')
  localErrors.value = errs
  return Object.keys(errs).length === 0
}

async function handleSubmit(): Promise<void> {
  if (!validate()) return

  const hasUploading = uploads.value.some((u) => u.status === 'uploading')
  if (hasUploading) return

  const ticket = await create(props.organisationId, {
    category: category.value as typeof SupportTicketCategory[keyof typeof SupportTicketCategory],
    subject: subject.value.trim(),
    body: body.value.trim(),
    attachment_ids: attachmentIds.value.length > 0 ? attachmentIds.value : undefined,
  })

  if (ticket) {
    resetForm()
    emit('created', ticket)
  }
}

// Merge server-side form errors into localErrors for display
const allErrors = computed<Record<string, string>>(() => {
  const merged = { ...localErrors.value }
  for (const [key, messages] of Object.entries(formErrors.value)) {
    if (messages.length > 0) merged[key] = messages[0]
  }
  return merged
})
</script>

<template>
  <SlideOver
    :open="open"
    :title="t('support.new_ticket')"
    :saving="submitting"
    @close="handleClose"
  >
    <div class="space-y-5">
      <!-- Category -->
      <div>
        <label for="ticket-category" class="block text-sm font-medium text-slate-300 mb-1.5">
          {{ t('support.form.category_label') }}
        </label>
        <select
          id="ticket-category"
          v-model="category"
          class="w-full bg-slate-800 border border-slate-700 text-white rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
          :class="{ 'border-red-500': allErrors.category }"
        >
          <option value="" disabled>{{ t('common.select_placeholder') }}</option>
          <option v-for="cat in categories" :key="cat.value" :value="cat.value">
            {{ cat.label }}
          </option>
        </select>
        <p v-if="allErrors.category" class="mt-1 text-xs text-red-400">{{ allErrors.category }}</p>
      </div>

      <!-- Subject -->
      <div>
        <label for="ticket-subject" class="block text-sm font-medium text-slate-300 mb-1.5">
          {{ t('support.form.subject_label') }}
        </label>
        <input
          id="ticket-subject"
          v-model="subject"
          type="text"
          maxlength="255"
          :placeholder="t('support.form.subject_placeholder')"
          class="w-full bg-slate-800 border border-slate-700 text-white rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none placeholder:text-slate-500"
          :class="{ 'border-red-500': allErrors.subject }"
        />
        <p v-if="allErrors.subject" class="mt-1 text-xs text-red-400">{{ allErrors.subject }}</p>
      </div>

      <!-- Description -->
      <div>
        <label class="block text-sm font-medium text-slate-300 mb-1.5">
          {{ t('support.form.description_label') }}
        </label>
        <MarkdownEditor
          v-model="body"
          :placeholder="t('support.form.description_placeholder')"
          :max-length="50000"
        />
        <p v-if="allErrors.description" class="mt-1 text-xs text-red-400">{{ allErrors.description }}</p>
        <p v-else-if="allErrors.body" class="mt-1 text-xs text-red-400">{{ allErrors.body }}</p>
      </div>

      <!-- Attachments -->
      <div>
        <label class="block text-sm font-medium text-slate-300 mb-1.5">
          {{ t('support.form.attachments_label') }}
        </label>
        <MultiFileDropZone
          v-model="files"
          :uploads="uploads"
          :max-size-mb="10"
          accepted-types="image/png,image/jpeg,application/pdf"
          :max-files="10"
          @add="handleFileAdd"
          @remove="handleFileRemove"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-end gap-3">
        <button
          type="button"
          :disabled="submitting"
          class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors disabled:opacity-50"
          @click="handleClose"
        >
          {{ t('support.form.cancel') }}
        </button>
        <button
          type="button"
          :disabled="submitting"
          class="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
          @click="handleSubmit"
        >
          <AppSpinner v-if="submitting" size="button" />
          {{ t('support.form.submit') }}
        </button>
      </div>
    </template>
  </SlideOver>
</template>
